<template>
  <div>
    <base-flex-spinner v-if="loading" class="mt-16" />
    <BaseContainer v-else class="p-5 pb-24">
      <div class="flex flex-row items-center gap-4 mb-2">
        <TheBackButton :title="backLabel" class="!p-0" />
        <PrescriptionRenewalStepper
          v-if="prescription"
          v-model:step="step"
          :furthest-step="furthestStep"
          :can-open="canOpen"
        />
      </div>
      <p v-if="!prescription" class="mt-4">
        Cette ordonnance est introuvable.
      </p>
      <div v-else class="flex flex-row">
        <prescription-visual :prescription="prescription" />
        <div class="flex flex-1 flex-col p-2">
          <template v-for="n in 4" :key="`${n}-content`">
            <div v-if="visitedSteps.includes(n)" v-show="step === n" class="pb-2">
              <prescription-renewal-plan
                v-if="n===1"
                v-model:dates="dates"
                v-model:start-date="startDate"
                v-model:end-date="endDate"
                :prescription="prescription"
                @next-step="goToStep(2)"
              />
              <prescription-renewal-operator
                v-if="n===2"
                v-model:selected="selectedOperatorCode"
                :team-access="teamAccess"
                :operator-codes="operatorCodes"
                :selected-operator-code="(selectedOperatorCode as string | undefined)"
                @next-step="goToStep(3)"
                @previous-step="goToStep(1)"
              />
              <prescription-renewal-notify
                v-if="n===3"
                v-model:selected-notification-channel="selectedNotificationChannel"
                v-model:customer-phone="customerPhone"
                v-model:notify-created="notifyCreated"
                v-model:notify-renewals="notifyRenewals"
                :dates="dates"
                :customer="prescription.customer"
                :notifications="notifications"
                @next-step="goToStep(4)"
                @previous-step="goToStep(2)"
              />
              <prescription-renewal-publish
                v-if="n===4"
                :dates="dates"
                :is-update="!!prescription?.renewal_plan"
                :end-date-formatted="endDateFormatted"
                :selected-operator-label="itemLabel(selectedOperatorCode)"
                :selected-notification-channel="selectedNotificationChannel"
                :notify-created="notifyCreated"
                :notifications="notifications"
                :notify-renewals="notifyRenewals"
                @on-publish="storeRenewalPlan()"
                @previous-step="goToStep(3)"
              />
            </div>
          </template>
        </div>
      </div>
    </BaseContainer>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import dayjs from 'dayjs';
import PrescriptionRenewalPlan from '@/prescription/components/PrescriptionRenewalPlan.vue';
import PrescriptionVisual from '@/prescription/components/PrescriptionVisual.vue';
import PrescriptionRenewalPublish from '@/prescription/components/PrescriptionRenewalPublish.vue';
import {notificationTemplateApi, prescriptionApi, prescriptionRenewalPlanApi, teamApi} from '@/container';
import PrescriptionRenewalOperator from '@/prescription/components/PrescriptionRenewalOperator.vue';
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {mapActions, mapState} from "pinia";
import {usePatientStore} from "@/stores/patient.store";
import dateHelper from "@/core/helpers/dateHelper";
import {Prescription} from "@/prescription/interfaces/Prescription";
import {useToastStore} from "@/stores/toast.store";
import PrescriptionRenewalNotify from "@/prescription/components/PrescriptionRenewalNotify.vue";
import LegacyAccess from "@/core/interfaces/Access";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import {PrescriptionRenewalPlanDate} from "@/prescription/interfaces/prescription-renewal-plan/PrescriptionRenewalPlan";
import {NotificationTemplate} from "@/notification-template/interfaces";
import TheBackButton from "@/core/components/navigation-bar/TheBackButton.vue";
import PrescriptionRenewalStepper from "@/prescription/components/PrescriptionRenewalStepper.vue";
import {renewalRemindersAreValid} from "@/prescription/helpers/renewalPlanHelper";

export default defineComponent({
    name: 'PrescriptionRenewalDetailScreen',
    components: {
        PrescriptionRenewalStepper,
        TheBackButton,
        BaseContainer,
        PrescriptionRenewalNotify,
        BaseFlexSpinner,
        PrescriptionRenewalOperator,
        PrescriptionRenewalPlan,
        PrescriptionVisual,
        PrescriptionRenewalPublish
    },
    data() {
        return {
            prescription: null as Prescription | null,
            dates: [] as Date[],
            teamAccess: [] as LegacyAccess[],
            operatorCodes: [] as string[],
            selectedOperatorCode: '' as string,
            selectedNotificationChannel: 'push' as 'push' | 'sms',
            notifyCreated: false,
            notifyRenewals: false,
            customerPhone: null as string | null,
            isRenewable: true,
            loading: true,
            step: 1,
            furthestStep: 1,
            visitedSteps: [1] as number[],
            startDate: dayjs().toDate() as Date,
            endDate: dayjs().add(3, 'month').toDate() as Date,
            notifications: [] as NotificationTemplate[]
        };
    },
    computed: {
        ...mapState(usePatientStore, ['customer']),
        endDateFormatted() {
            return dateHelper.shortFormat(this.endDate);
        },
        queryCustomerId() {
            return parseInt(this.$route.query.customerId as string);
        },
        /**
         * Name the page the back button leads to, from the one the plan was opened from.
         */
        backLabel(): string {
            const back = window.history.state?.back;

            if (typeof back !== 'string') {
                return 'Retour';
            }

            const route = this.$router.resolve(back);
            const name = String(route.name ?? '');

            if (name.endsWith('.renewal.show')) {
                return 'Plan de renouvellement';
            }

            if (name === 'patient-administration.renewal-plans.index') {
                return 'Plans de renouvellement';
            }

            if (name === 'patient-administration.renewal.index') {
                return 'Renouvellements';
            }

            if (route.matched.some(record => record.name === 'customer')) {
                return 'Fiche patient';
            }

            return 'Retour';
        }
    },
    watch: {
        /**
         * Mount a step on its first opening only, then keep it alive to preserve what it holds.
         */
        step(value: number) {
            if (!this.visitedSteps.includes(value)) {
                this.visitedSteps.push(value);
            }
        },
        '$route.params.prescriptionId': {
            immediate: true,

            handler(value) {
                if (this.queryCustomerId) {
                    this.read(this.queryCustomerId, value);
                } else if (this.customer) {
                    this.read(this.customer.id, value);
                }
            }
        }
    },
    mounted() {
        this.indexOperatorCodes();
    },
    methods: {
        ...mapActions(useToastStore, ['showSuccess', 'showError']),
        /**
         * Open a step, and keep every step up to the furthest one reached open.
         *
         * @param step - The step to open.
         */
        goToStep(step: number) {
            this.step = step;
            this.furthestStep = Math.max(this.furthestStep, step);
        },
        /**
         * Whether a step can be opened from the stepper.
         *
         * A step must have been reached, and every step before it must still be valid:
         * a plan emptied of its dates cannot be published by jumping to the last step.
         *
         * @param step - The step to open.
         */
        canOpen(step: number): boolean {
            if (step > this.furthestStep) {
                return false;
            }

            if (step > 1 && !this.dates.length) {
                return false;
            }

            if (step > 2 && !this.selectedOperatorCode) {
                return false;
            }

            return step <= 3 || renewalRemindersAreValid({
                notifyRenewals: this.notifyRenewals,
                notifyCreated: this.notifyCreated,
                channel: this.selectedNotificationChannel,
                phone: this.customerPhone
            });
        },
        /**
         * Read details of the prescription, necessary to have information missing in index ressource.
         */
        read(customerId: number, prescriptionKey: string) {
            this.loading = true;
            this.prescription = null;
            this.step = 1;
            this.furthestStep = 1;
            this.visitedSteps = [1];
            this.dates = [];
            this.selectedOperatorCode = '';
            this.notifyCreated = false;
            this.notifyRenewals = false;

            prescriptionApi()
                .read(customerId, prescriptionKey)
                .then(response => {
                    this.prescription = response;
                    this.isRenewable = response.renewable;
                    this.startDate = new Date(response.created_at);
                    this.customerPhone = response.customer.information?.phone || null;

                    if (response.renewal_plan) {
                        this.endDate = new Date(response.renewal_plan.end_at);
                        this.dates = response.renewal_plan.renewal_dates
                            .map((date: PrescriptionRenewalPlanDate) => new Date(date.renew_at));
                        this.selectedOperatorCode = response.renewal_plan.operator_code;
                        this.selectedNotificationChannel = response.renewal_plan.customer_notification_channel;
                        this.notifyRenewals = response.renewal_plan.notify_renewals;
                        this.furthestStep = 4;
                    } else {
                        this.endDate = dayjs(this.startDate).add(3, 'month').toDate();
                        this.selectedNotificationChannel = response.customer.onboarded ? 'push' : 'sms';
                    }
                })
                .catch(() => {
                    this.showError({content: "L'ordonnance n'a pas pu être chargée"});
                })
                .then(async () => {
                    if (!this.prescription) {
                        return;
                    }

                    this.notifications = await notificationTemplateApi().feature({
                        feature: 'renewal-plan',
                        customer_id: this.prescription.customer.id
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * Get item label.
         *
         * @param {string} code - The operator code.
         * @return {string}
         */
        itemLabel(code: string | null): string | null {
            const access: LegacyAccess | undefined = this.teamAccess?.find(
                (access: LegacyAccess) => !!code && access.operatorCode === code
            );

            if (access) {
                return `${(access as LegacyAccess).user.name} (${code})`;
            }

            return code;
        },
        /**
         * Get list of operator codes.
         */
        indexOperatorCodes() {
            teamApi().index()
                .then(response => {
                    this.teamAccess = response.team;

                    return teamApi().operatorCodes();
                })
                .then(response => {
                    this.operatorCodes = response;
                });
        },
        /**
         * Update the operator code of the renewal plan.
         *
         * @param code
         */
        updateOperatorCode(code) {
            this.selectedOperatorCode = code;
        },
        /**
         * Create a new renewal plan.
         *
         * @return {Promise<unknown>}
         */
        storeRenewalPlan(): Promise<void> | undefined {
            if (!this.prescription) {
                return Promise.resolve();
            }

            const params = {
                end_at: dayjs(this.endDate).format('YYYY-MM-DD'),
                dates: this.dates.map(date => {
                    return {renew_at: dayjs(date).format('YYYY-MM-DD')};
                }),
                prescription_key: this.prescription.key,
                operator_code: this.selectedOperatorCode,
                customer_notification_channel: this.selectedNotificationChannel,
                customer_phone: this.customerPhone,
                notify_created: this.notifyCreated ? 1 : 0,
                notify_renewals: this.notifyRenewals
            };

            if (this.prescription.renewal_plan) {
                return prescriptionRenewalPlanApi().update(this.prescription.renewal_plan, params)
                    .then(() => {
                        this.showSuccess({content: 'Le plan de renouvellement a été mis à jour avec succès'});
                    })
                    .catch(() => {
                        this.showError({content: "Une erreur s'est produite lors de la mise à jour du plan de renouvellement"});
                    })
                    .finally(() => {
                        this.$router.go(-1);
                    });
            } else {
                return prescriptionRenewalPlanApi().create(params)
                    .then(() => {
                        this.showSuccess({content: 'Le plan de renouvellement a été créé avec succès'});
                    })
                    .catch(() => {
                        this.showError({content: "Une erreur s'est produite lors de la création du plan de renouvellement"});
                    })
                    .finally(() => {
                        this.$router.go(-1);
                    });
            }
        }
    }
});
</script>

<style lang="postcss" scoped>
:deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(p) {
    @apply ml-1
}

:deep(h1) {
    @apply text-2xl font-bold
}

:deep(v-btn) {
    @apply px-10
}

:deep(.step-card) {
    @apply border-gray-200 border rounded-xl p-4 bg-white flex flex-col gap-y-3
}
</style>
